// Lightweight analytics dispatcher. Forwards to any installed provider
// (GA4 via gtag, GTM via dataLayer, Plausible, PostHog) without coupling
// to a specific one. Safe to call from SSR — guards on `window`.

import {
  completeFunnel,
  funnelDimensions,
  getFunnel,
  recordFunnelStep,
  startFunnelFromCta,
} from "./funnel";
import { gaEvent } from "./analytics/ga";

type EventParams = Record<string, string | number | boolean | undefined>;

type GtagFn = (command: "event", name: string, params?: EventParams) => void;
type PlausibleFn = (name: string, opts?: { props?: EventParams }) => void;
type PostHog = { capture: (name: string, props?: EventParams) => void };

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
    gtag?: GtagFn;
    plausible?: PlausibleFn;
    posthog?: PostHog;
  }
}

export type CtaId =
  | "book_strategy_meeting"
  | "how_pulse_works"
  | "download_product_sheet"
  | "download_formatguide"
  | "download_lead_magnet"
  | "view_pricing"
  | "contact_us"
  | "audience_lead_magnet_submit"
  | "audience_recommend_package"
  | "partner_inquiry_submit"
  | "guide_download_submit"
  | "prime_signature_reel_play"
  | "prime_video_play"
  | "prime_pricing_inline_click"
  | "prime_quick_quote_submit"
  | "prime_related_click"
  | "newsletter_subscribe"
  | "open_quote_calculator"
  // Konverterings-CTA:er (Prioritet A — CTA-hierarki)
  | "open_price_finder"
  | "price_finder_recommendation_cta"
  | "choose_path"
  | "book_pilot"
  // Sticky mobil-CTA med valmeny
  | "sticky_more"
  | "sticky_meeting"
  | "sticky_price"
  | "sticky_mail";




export interface TrackOptions extends EventParams {
  location?: string; // e.g. "header", "hero", "faq", "pulse_intro"
  label?: string; // human-readable button label
}

export function track(event: string, params: TrackOptions = {}): void {
  if (typeof window === "undefined") return;
  // Attach the active funnel context to every event so downstream tools can
  // group CTA → steps → conversion into a single journey without joins.
  const payload = {
    ...funnelDimensions(getFunnel()),
    ...params,
    timestamp: Date.now(),
  };

  try {
    // GTM dataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...payload });

    // GA4 — via vår isolerade gtag-instans (gaDataLayer). GTM:s instans
    // konsumerar inte gtag-kommandon i window.dataLayer, så ett direkt
    // window.gtag-anrop här skulle aldrig nå GA4. gaEvent är consent-gated.
    gaEvent(event, payload);

    // Plausible
    window.plausible?.(event, { props: payload });

    // PostHog
    window.posthog?.capture(event, payload);

    // Verification hook (dev only): logs every analytics event to the console
    // so QA can watch for cta_click / conversion events without GTM installed.
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.info(`[analytics] ${event}`, payload);
    }
  } catch {
    // Never let analytics break the UI
  }
}


export function trackCta(id: CtaId, options: TrackOptions = {}): void {
  // A CTA click (re)starts the funnel so downstream events get attributed
  // to the originating button/location.
  startFunnelFromCta({ cta_id: id, location: options.location, label: options.label });
  track("cta_click", { cta_id: id, ...options });
}

// ---------------------------------------------------------------------------
// Internlänk-/navigeringsevents (page-type → page-type). Mäter hur AEO-
// klustren matar konvertering: jämförelse → kontakt, artikel → tjänst,
// bransch → kontakt. Separata från cta_click så funnel-rapporter kan
// segmentera på innehållstyp.
// ---------------------------------------------------------------------------

export type InternalLinkEvent =
  | "comparison_to_contact_click"
  | "industry_to_contact_click"
  | "article_to_service_click"
  | "guide_download_click"
  | "related_link_click";

export function trackInternalLink(
  event: InternalLinkEvent,
  options: TrackOptions = {},
): void {
  track(event, options);
}

/**
 * Härleder vilket kontakt-CTA-event en sida ska skicka utifrån sin path.
 * Returnerar null när sidan inte är en jämförelse- eller branschsida
 * (då används vanlig cta_click istället).
 */
export function classifyContactCtaEvent(
  fromPath: string,
): InternalLinkEvent | null {
  const p = fromPath.replace(/\/$/, "");
  if (/^\/jamfor\//.test(p) || /-vs-/.test(p)) return "comparison_to_contact_click";
  if (/^\/branscher\//.test(p) || /^\/en\/industries\//.test(p))
    return "industry_to_contact_click";
  return null;
}

/** True om länkmålet är en kontaktsida (sv/en). */
export function isContactPath(to: string): boolean {
  return /(^|\/)(kontakt|contact)(\/|$)/.test(to);
}


/**
 * Record an intermediate step in the funnel (form view, field focus,
 * submit attempt, validation error, …). Fires a `funnel_step` event with
 * full funnel context attached.
 */
export function trackFunnelStep(step: string, options: TrackOptions = {}): void {
  recordFunnelStep(step);
  track("funnel_step", { step, ...options });
}

export type ConversionType =
  | "contact_form_submit"
  | "contact_details_completed"
  | "price_estimate_emailed"
  | "book_strategy_meeting"
  | "download_product_sheet"
  | "lead_magnet_download"
  | "lead_magnet_notify"
  | "quote_request_submitted"
  | "career_application_submit"
  | "callback_request";


export interface ConversionOptions extends TrackOptions {
  conversion_type: ConversionType;
  value?: number;
  currency?: string;
}

/**
 * Fire a `conversion` event (plus a typed sub-event) when a user completes
 * a meaningful goal — e.g. a successful contact form submit. Also closes
 * the current funnel and fires a `funnel_complete` event so the full
 * CTA → steps → conversion journey is captured atomically.
 */
export function trackConversion(options: ConversionOptions): void {
  const finalState = completeFunnel();
  const enriched: ConversionOptions = {
    ...options,
    // Snapshot the funnel onto the conversion itself (in addition to the
    // live context that `track()` attaches) so the conversion row in any
    // analytics tool is self-contained.
    ...(finalState
      ? {
          funnel_id: finalState.funnel_id,
          funnel_origin_cta_id: finalState.origin_cta_id,
          funnel_origin_location: finalState.origin_location,
          funnel_step_count: finalState.step_count,
          funnel_duration_ms: Date.now() - finalState.started_at,
          funnel_path: finalState.steps.join(" > "),
        }
      : {}),
  };
  track("conversion", enriched);
  track(options.conversion_type, enriched);
  if (finalState) track("funnel_complete", enriched);

  // GA4:s standardhändelse för lead — gör att inkomna leads kan sättas som
  // konvertering i Google Analytics utan extra konfiguration.
  if (isLeadConversion(options.conversion_type)) {
    track("generate_lead", {
      ...enriched,
      currency: options.currency ?? "SEK",
      ...visitorDimensions(),
    });
  }
}

const NON_LEAD_CONVERSIONS = new Set<string>([
  "product_sheet_download",
  "formatguide_download",
  "lead_magnet_download",
  // Steg 2 i kontaktformuläret är samma lead som redan räknats i steg 1.
  "contact_details_completed",
]);

function isLeadConversion(type: string): boolean {
  return !NON_LEAD_CONVERSIONS.has(type);
}

/** Kopplar GA-händelsen till samma besökar-/session-ID som adminpanelens leadresa. */
function visitorDimensions(): EventParams {
  if (typeof window === "undefined") return {};
  try {
    return {
      visitor_id: localStorage.getItem("fenny_vid") ?? undefined,
      session_id: sessionStorage.getItem("fenny_sid") ?? undefined,
    };
  } catch {
    return {};
  }
}

